import {
  AddPatientContainer,
  Texts,
  Component1,
  HeaderContainer,
  ButtonContainer,
  StyledButton,
} from '../style.js';
import InputSelect from 'src/components/shared/Form/Select/index.js';
import { useTheme } from '@emotion/react';
import InputComponent from 'src/components/shared/Form/Input/index.js';
import { palette, shadows } from 'src/config/theme.js';
import { alpha, IconButton, Box } from '@mui/material';
import DateTimePicker from 'src/components/App/DatePicker/index.js';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { addPatientRequest } from '../../store/appActions';
import { ReactComponent as CloseIcon } from 'src/assets/images/close-circle.svg';
import { ReactComponent as AddIcon } from 'src/assets/images/add-square.svg';
import ButtonComponent from 'src/components/shared/Button';
import { getUserFromToken } from 'src/DecodeToken.js';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

function AddPatient({ onSave, onClose, onPatientAdded }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [dateOfBirth, setdateOfBirth] = useState(null);
  const [selectValue, setSelectValue] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    contactNo: '',
    gender: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().trim().required('Full Name is required'),
    contactNo: Yup.string()
      .matches(/^\d{10}$/, 'Contact number must be 10 digits')
      .required('Contact Number is required'),
    gender: Yup.string().required('Gender is required'),
    dateOfBirth: Yup.mixed()
      .required('Date of Birth is required')
      .test('is-valid-date', 'Date of Birth is required', (val) => {
        if (!val) return false;
        return dayjs(val).isValid();
      })
      .test('not-in-future', 'Date of Birth cannot be in the future', (val) => {
        if (!val) return true;
        return dayjs(val).isBefore(dayjs().add(1, 'day'));
      }),
  });

  const formatDateToDDMMYYYY = (date) => {
    if (!date) return '';
    const d = dayjs(date);
    if (!d.isValid()) return '';
    return d.format('YYYY-MM-DD');
  };

  const handleSubmit = async (values, { resetForm }) => {
    const userId = getUserFromToken();
    if (!userId) {
      enqueueSnackbar('Failed to identify user. Please login again.', { variant: 'error' });
      return;
    }

    const patientData = {
      fullName: values.fullName,
      contactNo: values.contactNo,
      gender: values.gender,
      dateOfBirth: formatDateToDDMMYYYY(values.dateOfBirth),
      users: [userId],
    };

    try {
      await new Promise((resolve, reject) => {
        dispatch(addPatientRequest(patientData, resolve, reject));
      });
      if (typeof onPatientAdded === 'function') onPatientAdded();
      if (typeof onSave === 'function') onSave();
      if (typeof onClose === 'function') onClose();
      resetForm();
      enqueueSnackbar('Patient added successfully', { variant: 'success' });

    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to add patient', { variant: 'error' });
    }
  };

  return (
    <Formik
      initialValues={{ fullName: '', contactNo: '', gender: 'male', dateOfBirth: null }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, handleChange, setFieldValue }) => (
        <Form>
          <HeaderContainer>
            Add Patient
            <IconButton onClick={onClose}>
              <CloseIcon style={{ cursor: 'pointer' }} />
            </IconButton>
          </HeaderContainer>
          <AddPatientContainer isExpanded={selectValue === 'New Patient'}>
            <Component1>
              <Texts>Full Name</Texts>
              <InputComponent
                name="fullName"
                value={values.fullName}
                onChange={handleChange}
                error={touched.fullName && Boolean(errors.fullName)}
                helperText={touched.fullName && errors.fullName}
                placeholder="Enter Name"
                variant="outlined"
                fullWidth
                sx={{
                  backgroundColor: palette.gery.white,
                  boxShadow: shadows[1],
                  '& .MuiOutlinedInput-root': {
                    padding: '10px 0px',
                    borderRadius: '12px',
                    border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                    height: '44px',
                  },
                }}
              />
            </Component1>

            <Component1>
              <Texts>Contact Number</Texts>
              <InputComponent
                name="contactNo"
                value={values.contactNo}
                onChange={handleChange}
                error={touched.contactNo && Boolean(errors.contactNo)}
                helperText={touched.contactNo && errors.contactNo}
                placeholder="Enter Number"
                variant="outlined"
                fullWidth
                sx={{
                  backgroundColor: palette.gery.white,
                  boxShadow: shadows[1],
                  '& .MuiOutlinedInput-root': {
                    padding: '10px 0px',
                    borderRadius: '12px',
                    border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                    height: '44px',
                  },
                }}
              />
            </Component1>

            <Component1>
              <Texts>Gender</Texts>
              <InputSelect
                name="gender"
                showNone={false}
                value={values.gender}
                onChange={(selectedValue) => setFieldValue('gender', selectedValue)}
                options={[
                  { label: 'Male', value: 'male' },
                  { label: 'Female', value: 'female' },
                ]}
                error={touched.gender && Boolean(errors.gender)}
                helperText={touched.gender && errors.gender}
                variant="outlined"
                placeholder="Select Gender"
                sx={{
                  boxShadow: theme.shadows[1],
                  '& .MuiOutlinedInput-root': {
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '12px',
                  },
                }}
              />
            </Component1>

            <Component1>
              <Texts>Date of Birth</Texts>
              <DateTimePicker
                name="dateOfBirth"
                value={values.dateOfBirth}
                onChange={(date) => setFieldValue('dateOfBirth', date)}
                type="date"
                error={touched.dateOfBirth && Boolean(errors.dateOfBirth)}
                helperText={touched.dateOfBirth && errors.dateOfBirth}
                sx={{
                  height: '44px',
                  borderRadius: '12px',
                }}
              />
            </Component1>
          </AddPatientContainer>

          <ButtonContainer>
            <ButtonComponent
              variant="none"
              startIcon={<CloseIcon />}
              onClick={onClose}
              sx={{ ...theme.typography.h6 }}
            >
              Cancel
            </ButtonComponent>
            <StyledButton variant="outlined" type="submit" startIcon={<AddIcon />}>
              Save
            </StyledButton>
          </ButtonContainer>
        </Form>
      )}
    </Formik>
  );
}

export default AddPatient;
